import { flagToBool } from 'pano-utils';
import rgbHex from 'rgb-hex';
import type { HotspotEvents, LineEvents, TagLine } from '@types';
import { MainPlayer } from '@stores/krpPlayerStores';
import colorStringToHexAndAlpha from '@utils/colorStringToHexAndAlpha';
import colorStringToObj from '@utils/colorStringToObj';

export const addTagLine = (
  tagLine: TagLine,
  dragable?: boolean,
  enabled?: boolean,
  events?: LineEvents,
  titleEvents?: HotspotEvents
) => {
  if (!tagLine) {
    return;
  }
  const lineRGBA = colorStringToObj(tagLine.style.lineColor);
  const pointRGBA = colorStringToObj(tagLine.style.pointColor);

  MainPlayer.get()?.addLine({
    id: tagLine.id,
    startAth: tagLine.startAth,
    startAtv: tagLine.startAtv,
    endAth: tagLine.endAth === 0 ? tagLine.startAth : tagLine.endAth,
    endAtv: tagLine.endAtv === 0 ? tagLine.startAtv : tagLine.endAtv,
    lineWidth: tagLine.style.lineWidth,
    dragable,
    endPointMode: tagLine.style.pointMode,
    lineColor: Number.parseInt(
      `0x${rgbHex(lineRGBA.r, lineRGBA.g, lineRGBA.b)}`
    ),
    lineAlpha: lineRGBA.a,
    endPointColor: Number.parseInt(
      `0x${rgbHex(pointRGBA.r, pointRGBA.g, pointRGBA.b)}`
    ),
    endPointAlpha: pointRGBA.a,
    enabled,
    events,
  });

  const pos = MainPlayer.get()?.calcLineMiddlePoint(tagLine.id);
  const rotate = tagLine.titleSetting.enableRotate
    ? tagLine.titleSetting.rotate
    : MainPlayer.get()?.calcLineDegree(tagLine.id);
  let oy: number;
  switch (tagLine.titleSetting.alignMode) {
    case 1:
      oy = -20;
      break;
    case 2:
      oy = 20;
      break;
    default:
      oy = 0;
  }
  MainPlayer.get()?.addTextHotspot({
    id: `${tagLine.id}_title`,
    text: tagLine.title,
    ath: pos?.ath,
    atv: pos?.atv,
    autoResize: false,
    padding: '5',
    oy,
    distorted: flagToBool(tagLine.titleSetting.distorted),
    rotate,
    vCenter: true,
    font: {
      fontSize: tagLine.titleSetting.fontSize,
      fontColor: colorStringToHexAndAlpha(tagLine.titleSetting.fontColor).hex,
      fontAlpha: colorStringToHexAndAlpha(tagLine.titleSetting.fontColor).alpha,
    },
    background: {
      bgEnabled: true,
      bgColor: colorStringToHexAndAlpha(tagLine.titleSetting.bgColor).hex,
      bgAlpha: colorStringToHexAndAlpha(tagLine.titleSetting.bgColor).alpha,
      bgRoundedge: 3,
      borderWidth: 0,
    },
    events: titleEvents,
    visible:
      tagLine.startAth !== 0 &&
      tagLine.startAtv !== 0 &&
      tagLine.endAth !== 0 &&
      tagLine.endAtv !== 0 &&
      tagLine.title !== '',
  });
};

export const updateTagLine = (
  tagLine: TagLine,
  dragable?: boolean,
  enabled?: boolean,
  events?: LineEvents
) => {
  const lineRGBA = colorStringToObj(tagLine.style.lineColor);
  const pointRGBA = colorStringToObj(tagLine.style.pointColor);

  MainPlayer.get()?.updateLine({
    id: tagLine.id,
    startAth: tagLine.startAth,
    startAtv: tagLine.startAtv,
    endAth: tagLine.endAth === 0 ? tagLine.startAth : tagLine.endAth,
    endAtv: tagLine.endAtv === 0 ? tagLine.startAtv : tagLine.endAtv,
    lineWidth: tagLine.style.lineWidth,
    dragable,
    endPointMode: tagLine.style.pointMode,
    lineColor: Number.parseInt(
      `0x${rgbHex(lineRGBA.r, lineRGBA.g, lineRGBA.b)}`
    ),
    lineAlpha: lineRGBA.a,
    endPointColor: Number.parseInt(
      `0x${rgbHex(pointRGBA.r, pointRGBA.g, pointRGBA.b)}`
    ),
    endPointAlpha: pointRGBA.a,
    enabled,
    events,
  });

  const pos = MainPlayer.get()?.calcLineMiddlePoint(tagLine.id);
  const rotate = tagLine.titleSetting.enableRotate
    ? tagLine.titleSetting.rotate
    : MainPlayer.get()?.calcLineDegree(tagLine.id);
  let oy: number;
  switch (tagLine.titleSetting.alignMode) {
    case 1:
      oy = -20;
      break;
    case 2:
      oy = 20;
      break;
    default:
      oy = 0;
  }

  MainPlayer.get()?.updateTextHotspot({
    id: `${tagLine.id}_title`,
    text: tagLine.title,
    ath: pos?.ath,
    atv: pos?.atv,
    oy,
    distorted: flagToBool(tagLine.titleSetting.distorted),
    rotate,
    font: {
      fontSize: tagLine.titleSetting.fontSize,
      fontColor: colorStringToHexAndAlpha(tagLine.titleSetting.fontColor).hex,
      fontAlpha: colorStringToHexAndAlpha(tagLine.titleSetting.fontColor).alpha,
    },
    background: {
      bgEnabled: true,
      bgColor: colorStringToHexAndAlpha(tagLine.titleSetting.bgColor).hex,
      bgAlpha: colorStringToHexAndAlpha(tagLine.titleSetting.bgColor).alpha,
      bgRoundedge: 3,
      borderWidth: 0,
    },
    visible:
      tagLine.startAth !== 0 &&
      tagLine.startAtv !== 0 &&
      tagLine.endAth !== 0 &&
      tagLine.endAtv !== 0 &&
      tagLine.title !== '',
  });

  MainPlayer.get()?.call('updatescreen();');
};

export const removeTagLine = (id: string) => {
  MainPlayer.get()?.removeLine(id);
  MainPlayer.get()?.removeHotspotById(`${id}_title`);
};
